import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, apiGet } from "@/lib/api";
import { fmtEUR, fmtDate, creditsLabel } from "@/lib/format";
import { fmtDuration } from "@/lib/agenda";
import { StatusBadge } from "@/components/StatusBadge";
import { CompositePrestationDialog } from "@/components/dialogs/CompositePrestationDialog";
import { EditSessionDialog } from "@/components/dialogs/EditSessionDialog";
import { CompleteDialog } from "@/components/dialogs/CompleteDialog";
import { SearchField } from "@/components/SearchField";
import { DataUnavailable } from "@/components/pwa/OfflineBanner";
import { useResource } from "@/pwa/useResource";
import { useOnlineGuard } from "@/pwa/useOnlineGuard";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, CheckCircle2, XCircle, Trash2, RotateCcw, CalendarClock, Pencil, BookOpen, Eye } from "lucide-react";
import { toast } from "sonner";

const FILTERS = ["Tutte", "Pianificata", "Svolta", "Annullata"];

export default function Services() {
  const [filter, setFilter] = useState("Tutte");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [complete, setComplete] = useState(null);
  const [editSess, setEditSess] = useState(null);
  const [editV2, setEditV2] = useState(null);
  const nav = useNavigate();

  const { data: sd, unavailable, reload: load } = useResource(useCallback(async () => {
    const params = filter === "Tutte" ? {} : { status: filter };
    const s = await apiGet("/sessions", { params });
    // Calendar links are best-effort: a non-WG tenant returns 403 (Calendar not
    // available yet). It must never turn Prestazioni into a fake offline state.
    let calLinks = {};
    try { const c = await apiGet("/calendar/links"); calLinks = c.data || {}; } catch (e) { calLinks = {}; }
    let diaryMap = {};
    try { const dm = await apiGet("/lesson-diaries/session-status"); diaryMap = dm.data || {}; } catch (e) { diaryMap = {}; }
    return { data: { sessions: s.data, calLinks, diaryMap }, stale: s.stale };
  }, [filter]), [filter]);
  const sessions = sd?.sessions || [];
  const calLinks = sd?.calLinks || {};
  const diaryMap = sd?.diaryMap || {};
  const diaryLabel = (st) => (st === "completed" ? "Apri diario" : st === "draft" ? "Continua diario" : "Compila diario");
  const { guard } = useOnlineGuard();

  const filtered = sessions.filter((s) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (s.dog_names || []).join(" ").toLowerCase().includes(q)
      || (s.owner_name || "").toLowerCase().includes(q)
      || (s.service_type || "").toLowerCase().includes(q);
  });

  const setStatus = async (s, status) => {
    try {
      await api.patch(`/sessions/${s.id}/status`, { status });
      toast.success("Stato aggiornato");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (s) => {
    try { await api.delete(`/sessions/${s.id}`); toast.success("Eliminata"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Prestazioni</h1>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="prestazioni-agenda-btn" onClick={() => nav("/agenda")} className="rounded-full"><CalendarClock className="h-4 w-4 mr-1" />Agenda</Button>
          <Button data-testid="add-session-btn" onClick={guard(() => setOpen(true))} className="rounded-full"><Plus className="h-4 w-4 mr-1" />Nuova prestazione</Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex gap-1 rounded-full border border-border bg-card p-1 w-fit">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold ${filter === f ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{f}</button>
          ))}
        </div>
        <div className="sm:max-w-xs sm:flex-1">
          <SearchField value={query} onChange={setQuery} placeholder="Cerca per cane, cliente o servizio…" testid="prestazioni-search" />
        </div>
      </div>

      {unavailable ? <DataUnavailable /> : (
      <div className="space-y-2" data-testid="sessions-list">
        {filtered.map((s) => (
          <div key={s.id} className="rounded-2xl border border-border bg-card p-4">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <button onClick={() => s.dog_ids?.[0] && nav(`/cani/${s.dog_ids[0]}`)} className="font-semibold hover:underline text-left">
                  {(s.dog_names || []).join(", ") || "—"}
                </button>
                <span className="text-muted-foreground"> · {s.owner_name}</span>
                <div className="text-sm text-muted-foreground">
                  {s.service_type} · {fmtDate(s.date)}{s.start_time ? ` · ${s.start_time}` : ""} · <span data-testid={`svc-duration-${s.id}`}>{fmtDuration(s.duration_minutes) || "—"}</span>
                  {s.is_free && " · gratuita"}
                  {s.status === "Svolta" && s.credits_consumed > 0 && ` · ${creditsLabel(s.credits_consumed)}`}
                  {!s.package_linked && s.value > 0 && ` · ${fmtEUR(s.value)}`}
                  {s.billable && s.fin_outstanding > 0 && <span className="ml-1 text-[#8D6E63] font-semibold" data-testid={`svc-residual-${s.id}`}>· Da incassare {fmtEUR(s.fin_outstanding)}</span>}
                </div>
              </div>
              <StatusBadge value={s.status} />
              {s.overdue && <span data-testid={`overdue-${s.id}`} className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#FFF3E9] text-[#C2410C] border border-[#F5C6A5]">Da verificare</span>}
              {calLinks[s.id] && (
                <span data-testid={`cal-sync-${s.id}`} className="text-xs font-semibold px-2 py-0.5 rounded-full border border-border text-muted-foreground inline-flex items-center gap-1">
                  <CalendarClock className="h-3 w-3" />
                  {calLinks[s.id].sync_status === "synced" ? "Calendar" : calLinks[s.id].sync_status === "error" ? "Errore Calendar" : calLinks[s.id].sync_status === "missing" ? "Evento mancante" : calLinks[s.id].sync_status === "conflict" ? "Conflitto Calendar" : calLinks[s.id].sync_status === "pending" ? "Sync in attesa" : "Calendar"}
                </span>
              )}
            </div>
            <div className="mt-3 flex gap-2 flex-wrap">
              {(s.status === "Svolta" || diaryMap[s.id]) && s.dog_ids?.[0] && (
                <Button size="sm" variant="outline" data-testid={`diary-${s.id}`} className="rounded-full" onClick={() => nav(`/cani/${s.dog_ids[0]}?tab=lezioni&session=${s.id}`)}>
                  <BookOpen className="h-4 w-4 mr-1" />{diaryLabel(diaryMap[s.id])}
                </Button>
              )}
              {s.status === "Pianificata" && (
                <>
                  <Button size="sm" data-testid={`complete-${s.id}`} onClick={() => setComplete(s)} className="rounded-full"><CheckCircle2 className="h-4 w-4 mr-1" />Svolta</Button>
                  <Button size="sm" variant="outline" data-testid={`edit-${s.id}`} onClick={() => (s.engine === "v2" ? setEditV2(s) : setEditSess(s))} className="rounded-full"><Pencil className="h-4 w-4 mr-1" />Modifica</Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" data-testid={`cancel-${s.id}`} className="rounded-full"><XCircle className="h-4 w-4 mr-1" />Annulla</Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Annullare questa prestazione?</AlertDialogTitle>
                        <AlertDialogDescription asChild>
                          <div>
                            <span className="block">Cane: <b>{(s.dog_names || []).join(", ") || "—"}</b></span>
                            <span className="block">Servizio: <b>{s.service_type}</b></span>
                            <span className="block">Data: <b>{fmtDate(s.date)}{s.start_time ? ` · ${s.start_time}` : ""}</b></span>
                            <span className="block mt-2">La prestazione resterà nello storico con stato <b>Annullata</b> e non consumerà crediti del pacchetto.</span>
                          </div>
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Indietro</AlertDialogCancel>
                        <AlertDialogAction data-testid={`cancel-confirm-${s.id}`} onClick={() => setStatus(s, "Annullata")}>Conferma annullamento</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
              {(s.status === "Svolta" || s.status === "Annullata") && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid={`revert-${s.id}`} className="rounded-full"><RotateCcw className="h-4 w-4 mr-1" />Riporta a pianificata</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Riportare questa prestazione a Pianificata?</AlertDialogTitle>
                      <AlertDialogDescription asChild>
                        <div>
                          <span className="block">Cane: <b>{(s.dog_names || []).join(", ") || "—"}</b></span>
                          <span className="block">Servizio: <b>{s.service_type}</b></span>
                          <span className="block">Data: <b>{fmtDate(s.date)}{s.start_time ? ` · ${s.start_time}` : ""}</b></span>
                          {s.status === "Svolta" && (s.paid || 0) > 0
                            ? <span className="block mt-2 text-[#8D6E63]">Questa prestazione ha un movimento economico collegato (pagamento o credito cliente). Prima di riportarla a Pianificata devi risolvere il pagamento o il credito cliente associato.</span>
                            : s.status === "Svolta"
                            ? <span className="block mt-2">I crediti consumati ({s.credits_consumed}) verranno ripristinati sul pacchetto.</span>
                            : <span className="block mt-2">La prestazione tornerà <b>Pianificata</b>. Nessun credito verrà modificato.</span>}
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Indietro</AlertDialogCancel>
                      {!(s.status === "Svolta" && (s.paid || 0) > 0) && (
                        <AlertDialogAction data-testid={`revert-confirm-${s.id}`} onClick={() => setStatus(s, "Pianificata")}>Riporta a pianificata</AlertDialogAction>
                      )}
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              {s.engine === "v2" && s.status === "Svolta" && (
                <Button size="sm" variant="outline" data-testid={`view-${s.id}`} onClick={() => setEditV2(s)} className="rounded-full"><Eye className="h-4 w-4 mr-1" />Dettaglio</Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="ghost" data-testid={`delete-${s.id}`} className="rounded-full text-destructive"><Trash2 className="h-4 w-4" /></Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Eliminare definitivamente la prestazione?</AlertDialogTitle>
                    <AlertDialogDescription asChild>
                      <div>
                        <span className="block">Cane: <b>{(s.dog_names || []).join(", ") || "—"}</b></span>
                        <span className="block">Servizio: <b>{s.service_type}</b> · {fmtDate(s.date)}{s.start_time ? ` · ${s.start_time}` : ""}</span>
                        {s.status === "Svolta"
                          ? <span className="block mt-2 text-destructive font-semibold">Attenzione: la prestazione è <b>Svolta</b>. Eliminandola cambieranno Prestazioni, Ore erogate e Valore. Operazione irreversibile.</span>
                          : <span className="block mt-2">La prestazione verrà rimossa definitivamente dallo storico. Operazione irreversibile.</span>}
                        <span className="block mt-1 text-xs text-muted-foreground">Se ci sono pagamenti collegati, crediti consumati o un evento Calendar attivo, l'eliminazione verrà bloccata.</span>
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annulla</AlertDialogCancel>
                    <AlertDialogAction data-testid={`delete-confirm-${s.id}`} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => del(s)}>Elimina definitivamente</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-sm text-muted-foreground" data-testid="sessions-empty">{query.trim() ? "Nessuna prestazione trovata." : "Nessuna prestazione."}</p>}
      </div>
      )}

      <CompositePrestationDialog open={open} onOpenChange={setOpen} mode="prestazione" onSaved={load} />
      <CompositePrestationDialog open={!!editV2} onOpenChange={(v) => !v && setEditV2(null)} editSession={editV2} onSaved={load} />
      <EditSessionDialog open={!!editSess} onOpenChange={(v) => !v && setEditSess(null)} session={editSess} onSaved={load} />
      <CompleteDialog open={!!complete} onOpenChange={(v) => !v && setComplete(null)} session={complete} onDone={load} />
    </div>
  );
}
